import React, { Children, cloneElement } from 'react';
import styled from '@emotion/styled';
import css from '@styled-system/css';
import PropTypes from 'prop-types';

import { Glyph } from '../../iconography/Glyph';
import sx from '../../internal/styles/sx';
import CircledNumber from '../CircledNumber';
import { Information } from '../icons/glyphs';
import { StyledItem } from './ListItem';

const StyledList = styled('ul')(
  css({
    fontFamily: 'body',
    flexDirection: 'column',
    padding: 0,
    m: 0,
    [`${StyledItem}:last-child`]: {
      pb: 0,
    },
  })
);

export const Dot = styled('div')(
  css({
    backgroundColor: 'black',
    borderRadius: '50%',
    width: '5px',
    height: '5px',
    mt: '8px',
  }),
  sx
);

const Markers = {
  default: () => <Dot />,
  included: ({ size }) => (
    <Glyph
      category="UIActions"
      name="Check"
      size={size}
      aria-label="check-icon"
      color="positive.default"
    />
  ),
  excluded: ({ size }) => (
    <Glyph
      category="UIActions"
      name="Clear"
      size={size}
      color="negative.default"
    />
  ),
  optional: ({ size }) => (
    <Glyph
      category="UIActions"
      name="Check"
      size={size}
      aria-label="check-icon"
      color="neutrals.3"
    />
  ),
  information: ({ size }) => <Information size={size} color="neutrals.3" />,
};

const OrderedListIcon = ({ index, size }) => (
  <CircledNumber aria-label="circled-icon" size={size}>
    {index + 1}
  </CircledNumber>
);

const ListIcon = ({ marker = 'default', size }) => Markers[marker]({ size });

ListIcon.propTypes = {
  marker: PropTypes.oneOf([
    'default',
    'included',
    'excluded',
    'optional',
    'information',
  ]),
};

const Base = ({ children }) => {
  return <StyledList>{children}</StyledList>;
};

const List = ({ children, ordered = false, size = 'small' }) => {
  return (
    <Base as={ordered ? 'ol' : 'ul'}>
      {Children.map(children, (listItem, index) => {
        return cloneElement(listItem, {
          size,
          icon: ordered ? (
            <OrderedListIcon index={index} size={size} />
          ) : (
            <ListIcon marker={listItem.props.marker} size={size} />
          ),
        });
      })}
    </Base>
  );
};

List.propTypes = {
  /**
   * The <ListItem> elements you want to show as a list.
   */
  children: PropTypes.array.isRequired,
  /**
   * Kind of list type: ul|ol
   */
  ordered: PropTypes.bool,
  /**
   * Size small or medium
   */
  size: PropTypes.oneOf(['small', 'medium']),
};

List.Base = Base;
export default List;
